/**
 * ExecutiveView.jsx — Chrome-less, boardroom / PDF-ready PeopleLab presentation.
 *
 * Strips out the app sidebar, tabs, exports, and admin UI. Renders a clean
 * single-page view with logo, simulation name, and the same HRLabReport content
 * (Executive Summary → Reaction Overview → Friction Map → Recommended Actions →
 * Reliability). Designed to look great in meetings and as a printed/PDF export.
 *
 * URL: /analysis/executive?simulation=<id>
 */
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Loader2 } from 'lucide-react';
import api from '../lib/api';
import HRLabReport from '../components/HRLabReport';
import { SynPopLogo } from '../components/Layout';
import { formatDate } from '../lib/formatters';

export default function ExecutiveView() {
  const [searchParams] = useSearchParams();
  const simulationId = searchParams.get('simulation');
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!simulationId) {
      setLoading(false);
      return;
    }
    api
      .get(`/simulations/${simulationId}`)
      .then((res) => setSimulation(res.data))
      .catch(() => setSimulation(null))
      .finally(() => setLoading(false));
  }, [simulationId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!simulation) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-background p-8">
        <p className="text-sm text-slate-500 mb-4">Simulation not found.</p>
        <Link to="/dashboard">
          <Button variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Dashboard
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background print:bg-white" data-testid="executive-view">
      {/* Top action bar — hidden in print */}
      <div className="sticky top-0 z-10 bg-white/80 dark:bg-card/80 backdrop-blur border-b border-slate-200 dark:border-border print:hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-3 flex items-center justify-between">
          <Link to={`/analysis?simulation=${simulationId}`} data-testid="executive-back-btn">
            <Button variant="ghost" size="sm" className="gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Back to Analysis
            </Button>
          </Link>
          <Button
            onClick={() => window.print()}
            size="sm"
            variant="outline"
            className="gap-1.5"
            data-testid="executive-print-btn"
          >
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      {/* Cover header */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-10 pb-6 print:pt-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-0">
            <SynPopLogo size="lg" />
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-6 mb-2 font-semibold">
              Executive Summary · Workforce Reaction Lab
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
              {simulation.name}
            </h1>
            {simulation.completed_at && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                Completed {formatDate(simulation.completed_at, 'long')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Report body — reuse HRLabReport */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 pb-16 space-y-6">
        <HRLabReport simulation={simulation} simulationId={simulation.id} />
        <p className="text-[10px] text-slate-400 dark:text-slate-500 pt-6 border-t border-slate-200 dark:border-border leading-relaxed">
          Generated by SynPop · Synthetic population simulation. Results are directional and
          synthetic. Segment data masked below 30 respondents.
        </p>
      </div>
    </div>
  );
}
